import {
  TextField,
  TextInput,
  required,
  AutocompleteInput,
  ReferenceArrayInput,
  SelectInput,
  useNotify,
  useRecordContext,
  BulkExportButton,
  downloadCSV,
  FormDataConsumer,
} from "react-admin";
import {
  MyCreate as Create,
  MyEdit,
} from "../ra-lb-tools/components/myMutator";
import SecurityIcon from "@mui/icons-material/Security";
import { MyList } from "../ra-lb-tools/components/myList";
import { RecordSummary, MyShow } from "../ra-lb-tools/components/MyShow";
import { LabeledColumn } from "../ra-lb-tools/components/labeledColumn";
import { MyEditButton as EditButton } from "../ra-lb-tools/components/myEditButton";
import { MyDeleteButton } from "../ra-lb-tools/components/myDeleteButton";
import {
  MyTabbedShowLayout,
  Tab,
} from "../ra-lb-tools/components/myTabbedShowLayout";
import omit from "lodash/omit";
import jsonExport from "jsonexport/dist";
import { Box } from "@mui/material";
import { humanize, objectMap } from "../ra-lb-tools/utils/tools";
import { FieldsCleaner } from "../ra-lb-tools/components/fieldsCleaner";

export const PolicyIcon = SecurityIcon;

const policyFilters = [
  <TextInput label="Subject (Role or id)" source="v0" alwaysOn key={2} />,
  <TextInput label="Resource" source="v1" alwaysOn key={3} />,
  <TextInput label="Action" source="v2" alwaysOn key={4} />,
  <TextInput label="type" source="ptype" key={1} />,
];

const policyTypes = [
  { id: "p", name: "P" },
  { id: "p2", name: "P2" },
];

const is_protected_policy = (record) =>
  (record.v0 === "superuser" && record.v1 === "*" && record.v2 === "*") ||
  record.v2 === "backend/permission/*";

const PolicyDeleteButton = () => {
  const notify = useNotify();
  const record = useRecordContext();

  if (!record) return null;
  if (is_protected_policy(record)) return null;

  return (
    <MyDeleteButton
      mutationOptions={{
        meta: {
          notify,
        },
      }}
    />
  );
};

const PolicyEditButton = () => {
  const record = useRecordContext();

  if (!record) return null;
  if (is_protected_policy(record)) return null;

  return <EditButton />;
};

const exporter = (policies) => {
  const policiesForExport = policies.map((policy) => omit(policy, ["id"]));

  jsonExport(policiesForExport, (err, csv) => {
    downloadCSV(csv, "policies");
  });
};

export const PolicyList = (props) => {
  const resource = "policies";

  return (
    <MyList
      {...props}
      filters={policyFilters}
      resource={resource}
      customRowButtons={["edit", "delete"]}
      enableImport={true}
      dataGridProps={{
        bulkActionButtons: <BulkExportButton />,
      }}
      exporter={exporter}
    >
      <TextField source="id" />
      <TextField source="ptype" label="Type" />
      <TextField source="v0" label="Subject (Role or id)" />
      <TextField source="v1" label="Resource" />
      <TextField source="v2" label="Action" />
      <TextField source="v3" />
      <TextField source="v4" />
      <TextField source="v5" />
      <TextField source="description" />
      <PolicyDeleteButton />
      <PolicyEditButton />
    </MyList>
  );
};

export const PolicyShow = () => {
  return (
    <MyShow>
      <MyTabbedShowLayout>
        <Tab label="Summary" can={true}>
          <RecordSummary sx={{ justifyContent: "start" }}>
            <LabeledColumn>
              <TextField source="id" />
              <TextField source="ptype" label="Type" />
              <TextField source="v0" label="Subject (Role or id)" />
              <TextField source="v1" label="Resource" />
              <TextField source="v2" label="Action" />
            </LabeledColumn>
            <LabeledColumn>
              <TextField source="v3" />
              <TextField source="v4" />
              <TextField source="v5" />
              <TextField source="description" />
            </LabeledColumn>
          </RecordSummary>
        </Tab>
      </MyTabbedShowLayout>
    </MyShow>
  );
};

const field_mapping = {
  p: {
    resources: "v1",
    actions: "v2",
  },
  p2: {
    domain: "v1",
    resources: "v2",
    actions: "v3",
  },
};

const PolicyDescription = () => {
  return (
    <FormDataConsumer>
      {({ formData }) => {
        const fields = field_mapping[formData.ptype] || field_mapping["p"];

        const data = {
          subject: formData["v0"],
          resource: formData[fields.resources],
          action: formData[fields.actions],
          domain: formData[fields.domain],
        };

        if (
          data.subject &&
          data.resource &&
          data.action &&
          (data.domain || !fields.domain)
        ) {
          data.api = data.action.split("/")[0] || "*";
          data.model = data.action.split("/")[1] || "*";
          data.command = data.action.split("/")[2] || "*";

          if (data.resource == data.model) {
            data.resource = "";
          }

          if (data.domain == data.api) {
            data.domain = "";
          }

          const expandedData = objectMap({
            obj: data,
            valueCallback: (value, key) =>
              value == "*"
                ? key == "subject"
                  ? "anyone"
                  : `any ${key}`
                : value,
          });

          const domain_str = expandedData.domain
            ? ` for ${expandedData.domain}`
            : "";

          const resource_str = expandedData.resource
            ? ` for ${expandedData.resource}`
            : "";

          const description = `Explanation: ${humanize(
            expandedData.subject
          )} is allowed to execute ${expandedData.command}, for ${
            expandedData.model
          }, on ${expandedData.api}
          ${resource_str}${domain_str}.`;

          return <Box>{description}</Box>;
        }
      }}
    </FormDataConsumer>
  );
};

const PolicyMutatorFields = () => {
  const filterToQuery = (searchText) => ({ id: `${searchText}` });

  return (
    <FormDataConsumer>
      {({ formData }) => {
        const fields = field_mapping[formData.ptype] || field_mapping["p"];

        return (
          <>
            {fields.domain && (
              <TextInput
                source={fields.domain}
                validate={required()}
                label="Domain"
              />
            )}
            <ReferenceArrayInput
              source={fields.resources}
              reference="policy-resources"
            >
              <AutocompleteInput
                filterToQuery={filterToQuery}
                optionText="id"
                optionValue="id"
                label="Resource"
              />
            </ReferenceArrayInput>
            <ReferenceArrayInput
              source={fields.actions}
              reference="policy-actions"
            >
              <AutocompleteInput
                filterToQuery={filterToQuery}
                optionText="id"
                optionValue="id"
                label="Action"
              />
            </ReferenceArrayInput>
          </>
        );
      }}
    </FormDataConsumer>
  );
};

export const PolicyCreate = (props) => {
  return (
    <Create {...props} footer={<PolicyDescription />}>
      <SelectInput
        source="ptype"
        choices={policyTypes}
        label="Type"
        defaultValue={"p"}
        isRequired={true}
      />
      <TextInput
        source="v0"
        validate={required()}
        label="Subject (Role or id)"
      />
      <PolicyMutatorFields />
      <FieldsCleaner watch={"ptype"} clear={["v1", "v2", "v3", "v4", "v5"]} />
    </Create>
  );
};

export const PolicyEdit = (props) => {
  return (
    <MyEdit {...props} footer={<PolicyDescription />}>
      <SelectInput source="ptype" choices={policyTypes} label="Type" disabled />
      <TextInput
        source="v0"
        validate={required()}
        label="Subject (Role or id)"
      />
      <PolicyMutatorFields />
    </MyEdit>
  );
};
